import { h, Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import Image from 'utils/image';
import Icon from 'utils/icon';
import { useKitProduct } from './product-context';
import './product-selector.css';

const SizeDropdown = ({
  values,
  name,
  index,
  selectedVariant,
  selectedProduct,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { handleSizeChange } = useKitProduct();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div className="w-full relative mb-[8px]" ref={dropdownRef}>
      <div
        className="flex flex-row justify-between pl-[10px] pr-[10px] w-full border-1 border-grey-400 rounded-[3px] text-h6 text-grey-600 items-center cursor-pointer"
        ariaLabel={`Select ${name}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-grey-600 text-h6 py-[6px]">{`${
          selectedVariant.selectedOptions.find(
            (option) => option.name === 'Size',
          ).value
        }`}</span>
        <Icon id="chevron-down" className="h-[10px] w-[10px]" />
      </div>
      <div
        className={`max-h-[200px] overflow-y-scroll bg-white border-1 border-grey-400 rounded-b-[3px] border-t-0 absolute w-full z-1 ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        {values.map((value) => {
          // Check if the size is available in the selected product
          const matchedVariant = selectedProduct.variants.find(
            (variant) =>
              variant.selectedOptions.find((option) => option.name === 'Size')
                .value === value,
          );

          return (
            <div
              className={`pl-[10px] cursor-pointer flex flex-row items-center pt-[9px] pb-[9px] hover:bg-tan-100 ${
                !matchedVariant.available &&
                'opacity-50 bg-tan-100 hover:bg-tan-100 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!matchedVariant.available) return;

                handleSizeChange({ index, size: value });
                setIsOpen(false);
              }}
            >
              <p className="text-h6 text-grey-600">{value}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ColorDropdown = ({
  values,
  name,
  index,
  selectedVariant,
  selectedProduct,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { handleColorChange } = useKitProduct();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div className="w-full relative mb-[8px]" ref={dropdownRef}>
      <div
        className="flex flex-row justify-between gap-[5px] pl-[10px] pr-[10px] w-full border-1 border-grey-400 rounded-[3px] text-h6 text-grey-600 items-center cursor-pointer"
        ariaLabel={`Select ${name}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-grey-600 text-h6 py-[6px]">{`${
          selectedVariant.selectedOptions.find(
            (option) => option.name === 'Color',
          ).value
        }`}</span>
        <Icon id="chevron-down" className="h-[10px] w-[10px]" />
      </div>
      <div
        className={`max-h-[200px] overflow-y-scroll bg-white border-1 border-grey-400 rounded-b-[3px] border-t-0 absolute w-full z-1 ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        {values.map((value) => {
          const selectedSizeValue = selectedVariant.selectedOptions.find(
            (option) => option.name === 'Size',
          ).value;

          const selectedProductLengthTag = selectedProduct.lengthTag;

          if (value.lengthTag !== selectedProductLengthTag) return null;

          const relevantVariant = value.variants.find((variant) => {
            return (
              variant.selectedOptions.find((option) => option.name === 'Size')
                .value === selectedSizeValue &&
              value.lengthTag === selectedProductLengthTag
            );
          });

          return (
            <div
              className={`pl-[10px] cursor-pointer flex flex-row items-center pt-[9px] pb-[9px] hover:bg-tan-100 ${
                !relevantVariant?.available &&
                'opacity-50 bg-tan-100 hover:bg-tan-100 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!relevantVariant.available) return;
                handleColorChange({ index, handle: value.handle });
                setIsOpen(false);
              }}
            >
              <img
                src={value.swatchUrl}
                className="rounded-full mr-[5px] w-[19px]"
              />
              <p className="text-h6 text-grey-600">
                {
                  value.options.find((option) => option.name === 'Color')
                    .values[0]
                }
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TypeDropdown = ({ values, selectedProduct, kitIndex }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { handleProductChange } = useKitProduct();
  const uniqueTagSet = new Set();

  values.forEach((item) => {
    uniqueTagSet.add(item.lengthTag);
  });

  const uniqueTagArray = [...uniqueTagSet];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownRef]);

  if (!selectedProduct.lengthTag) return;
  return (
    <div className="w-full relative mb-[8px]" ref={dropdownRef}>
      <div
        className="flex flex-row justify-between pl-[10px] pr-[10px] w-full border-1 border-grey-400 rounded-[3px] text-h6 text-grey-600 items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-grey-600 text-h6 py-[6px]">
          Length: {selectedProduct.lengthTag.split('::')[1]}
        </span>
        <Icon id="chevron-down" className="h-[10px] w-[10px]" />
      </div>
      <div
        className={`max-h-[200px] overflow-y-scroll bg-white border-1 border-grey-400 rounded-b-[3px] border-t-0 absolute w-full z-1 ${
          isOpen ? 'block' : 'hidden'
        }`}
      >
        {uniqueTagArray.map((tag) => (
          <div
            className="pl-[10px] cursor-pointer flex flex-row items-center pt-[9px] pb-[9px] hover:bg-tan-100"
            onClick={() => handleProductChange(kitIndex, tag)}
          >
            <p className="text-h6 text-grey-600">
              {tag.replace('::', ': ').replace('length', 'Length')}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const KitProductSelector = ({
  selectedProduct,
  selectedVariant,
  groupedProducts,
  index,
}) => {
  const sizeValues = selectedProduct.options.find(
    (option) => option.name === 'Size',
  ).values;
  const imageRef = useRef(null);

  let selectorHeading = selectedProduct.title;
  if (selectedProduct.title.includes(':')) {
    selectorHeading = selectedProduct.title.split(':')[0];
  }

  return (
    <div className="grid grid-cols-[154px_1fr] gap-[12px]">
      <a href={selectedProduct.url}>
        <SwitchTransition>
          <CSSTransition
            key={selectedVariant.image.url}
            nodeRef={imageRef}
            addEndListener={(done) =>
              imageRef.current.addEventListener('transitionend', done, false)
            }
            classNames="fade"
          >
            <Image
              src={selectedVariant.image.url}
              srcWidths={{ sm: 640, md: 768, lg: 1024, xl: 1280, '2xl': 1536 }}
              additionalClasses="kit-product-seletor__image min-h-[200px] object-cover"
              nodeRef={imageRef}
            />
          </CSSTransition>
        </SwitchTransition>
      </a>

      <div>
        <p className="mb-[12px] text-s3 sm:mb-[10px] sm:text-s2">
          {selectorHeading}
        </p>
        <TypeDropdown
          kitIndex={index}
          selectedProduct={selectedProduct}
          values={groupedProducts}
        />
        <ColorDropdown
          values={groupedProducts}
          name="Color"
          index={index}
          selectedVariant={selectedVariant}
          selectedProduct={selectedProduct}
        />
        <SizeDropdown
          values={sizeValues}
          name="Size"
          index={index}
          groupedProducts={groupedProducts}
          selectedProduct={selectedProduct}
          selectedVariant={selectedVariant}
        />
      </div>
    </div>
  );
};

export default KitProductSelector;